/**
 * MonoText — inline monospace text using the Fluent token-defined font stack.
 *
 * Replaces 100+ inline `'Cascadia Code', 'JetBrains Mono', monospace` rolls.
 *
 * Use when: rendering an ID, EVSE, timestamp, hex value, or short code
 * fragment inline with body text. For boxed/bordered IDs use `IDChip`. For
 * multi-line code/JSON use `CodeBlock`.
 */
import type { ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

type MonoSize = 'small' | 'medium';
type MonoTone = 'default' | 'muted' | 'brand';

const useStyles = makeStyles({
  root: {
    fontFamily: tokens.fontFamilyMonospace,
  },
  small: { fontSize: tokens.fontSizeBase200 },
  medium: { fontSize: tokens.fontSizeBase300 },
  default: { color: tokens.colorNeutralForeground1 },
  muted: { color: tokens.colorNeutralForeground3 },
  brand: { color: tokens.colorBrandForeground1 },
});

interface MonoTextProps {
  children: ReactNode;
  size?: MonoSize;
  tone?: MonoTone;
  className?: string;
}

export function MonoText({ children, size = 'small', tone = 'default', className }: MonoTextProps) {
  const styles = useStyles();
  return (
    <span className={mergeClasses(styles.root, styles[size], styles[tone], className)}>
      {children}
    </span>
  );
}
